/**
 * Хуки задач v2. Без оптимистики: мутации только инвалидируют кеш, состояние
 * списка меняется после того, как бэк подтвердил.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  approveTaskV2,
  assignTaskUserV2,
  bulkTasksV2,
  completeTaskV2,
  createTaskV2,
  deleteTaskV2,
  fetchTaskBoardV2,
  fetchTaskCalendarV2,
  fetchTaskCardV2,
  fetchTaskV2,
  fetchTasksV2,
  fetchTaskTimerV2,
  moveTaskToStageV2,
  pauseTaskTimerV2,
  rejectTaskV2,
  renewTaskV2,
  setTaskStatusV2,
  startTaskTimerV2,
  stopTaskTimerV2,
  unassignTaskUserV2,
  updateTaskV2,
  type FetchTasksV2Params,
  type TaskV2AssigneeRole,
  type TaskV2BulkRequest,
  type TaskV2Patch,
  type TaskV2Write,
} from '../api/tasks-v2';
import { useActiveTimerV2Store } from './stores/active-timer-v2';

export const tasksV2Keys = {
  all: ['tasks-v2'] as const,
  list: (params: FetchTasksV2Params) =>
    [...tasksV2Keys.all, 'list', params] as const,
  detail: (id: string) => [...tasksV2Keys.all, 'detail', id] as const,
  card: (id: string) => [...tasksV2Keys.all, 'card', id] as const,
  board: (groupId: string | undefined) =>
    [...tasksV2Keys.all, 'board', groupId ?? 'by-status'] as const,
  calendar: (from: string, to: string, extra?: Record<string, unknown>) =>
    [...tasksV2Keys.all, 'calendar', from, to, extra ?? {}] as const,
  timer: (id: string) => [...tasksV2Keys.all, 'timer', id] as const,
};

/* --------------------------------------------------------------- queries --- */

export function useTasksV2(params: FetchTasksV2Params = {}) {
  return useQuery({
    queryKey: tasksV2Keys.list(params),
    queryFn: () => fetchTasksV2(params),
    // Страницы листаются без мигания пустотой.
    placeholderData: keepPreviousData,
  });
}

export function useTaskV2(id: string | null | undefined) {
  return useQuery({
    queryKey: tasksV2Keys.detail(id ?? ''),
    queryFn: () => fetchTaskV2(id as string),
    enabled: Boolean(id),
  });
}

export function useTaskCardV2(id: string | null | undefined) {
  return useQuery({
    queryKey: tasksV2Keys.card(id ?? ''),
    queryFn: () => fetchTaskCardV2(id as string),
    enabled: Boolean(id),
  });
}

export function useTaskBoardV2(params: {
  groupId?: string;
  itemsPerColumn?: number;
  enabled?: boolean;
}) {
  const { enabled = true, ...rest } = params;

  return useQuery({
    queryKey: tasksV2Keys.board(rest.groupId),
    queryFn: () => fetchTaskBoardV2(rest),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useTaskCalendarV2(params: {
  deadlineFrom: string;
  deadlineTo: string;
  responsibleId?: string;
  groupId?: string;
  enabled?: boolean;
}) {
  const { enabled = true, deadlineFrom, deadlineTo, ...rest } = params;

  return useQuery({
    queryKey: tasksV2Keys.calendar(deadlineFrom, deadlineTo, rest),
    queryFn: () => fetchTaskCalendarV2({ deadlineFrom, deadlineTo, ...rest }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/* ------------------------------------------------------------- mutations --- */

/** Инвалидация всего дерева задач: список, доска, календарь, карточки. */
function useInvalidateTasksV2() {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: tasksV2Keys.all });
}

export function useCreateTaskV2() {
  const invalidate = useInvalidateTasksV2();

  return useMutation({
    mutationFn: (payload: TaskV2Write) => createTaskV2(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateTaskV2() {
  const invalidate = useInvalidateTasksV2();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TaskV2Patch }) =>
      updateTaskV2(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteTaskV2() {
  const invalidate = useInvalidateTasksV2();

  return useMutation({
    mutationFn: (id: string) => deleteTaskV2(id),
    onSuccess: invalidate,
  });
}

/**
 * Перенос карточки на доске. Колонки бывают двух видов: у стадийных есть
 * `stageId`, у синтетических (доска без `groupId`) его нет — там двигаем
 * задачу сменой статуса.
 */
export function useMoveTaskV2() {
  const invalidate = useInvalidateTasksV2();

  return useMutation({
    mutationFn: ({
      id,
      stageId,
      status,
    }: {
      id: string;
      stageId?: string | null;
      status?: number;
    }) =>
      stageId
        ? moveTaskToStageV2(id, stageId)
        : setTaskStatusV2(id, status as number),
    onSuccess: invalidate,
  });
}

export function useTaskActionsV2() {
  const invalidate = useInvalidateTasksV2();

  const complete = useMutation({
    mutationFn: (id: string) => completeTaskV2(id),
    onSuccess: invalidate,
  });
  const renew = useMutation({
    mutationFn: (id: string) => renewTaskV2(id),
    onSuccess: invalidate,
  });
  const approve = useMutation({
    mutationFn: (id: string) => approveTaskV2(id),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      rejectTaskV2(id, reason),
    onSuccess: invalidate,
  });

  return { complete, renew, approve, reject };
}

/* ------------------------------------------------------------- команда --- */

/**
 * Соисполнители и наблюдатели. В теле задачи их полей нет — состав пишется
 * отдельным маршрутом по одному человеку за запрос, поэтому переключение это
 * две разные мутации, а не PATCH задачи. Снятие идёт только по `userId`: роль
 * в маршруте удаления не участвует.
 */
export function useTaskAssigneeActionsV2(id: string | undefined) {
  const qc = useQueryClient();
  const taskId = id ?? '';

  const refresh = () =>
    qc.invalidateQueries({ queryKey: tasksV2Keys.card(taskId) });

  const assign = useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: TaskV2AssigneeRole;
    }) => assignTaskUserV2(taskId, userId, role),
    onSuccess: refresh,
  });

  const unassign = useMutation({
    mutationFn: (userId: string) => unassignTaskUserV2(taskId, userId),
    onSuccess: refresh,
  });

  return {
    assign,
    unassign,
    isBusy: assign.isPending || unassign.isPending,
  };
}

/* --------------------------------------------------------------- таймер --- */

/**
 * Статус таймера задачи. Опрашивать по интервалу не нужно: пока таймер идёт,
 * секунды рисует локальный тик у вьюшки, а после каждого действия статус
 * перечитывается сам.
 */
export function useTaskTimerV2(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: tasksV2Keys.timer(id ?? ''),
    queryFn: () => fetchTaskTimerV2(id as string),
    enabled: enabled && Boolean(id),
  });
}

/**
 * Старт, пауза и стоп. Пауза и стоп — разные вещи: пауза оставляет сессию
 * таймера открытой (её продолжает тот же `start`), а стоп закрывает интервал и
 * пишет запись в `/elapsed`. Поэтому после стопа перечитываем ещё и карточку со
 * списаниями — там меняется факт.
 */
export function useTaskTimerActionsV2(id: string | undefined) {
  const qc = useQueryClient();
  const taskId = id ?? '';

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: tasksV2Keys.timer(taskId) }),
      qc.invalidateQueries({ queryKey: tasksV2Keys.card(taskId) }),
      qc.invalidateQueries({ queryKey: tasksV2Keys.detail(taskId) }),
    ]);
  };

  const start = useMutation({
    mutationFn: () => startTaskTimerV2(taskId),
    onSuccess: async () => {
      useActiveTimerV2Store.getState().setTaskId(taskId);
      await refresh();
    },
  });
  const pause = useMutation({
    mutationFn: () => pauseTaskTimerV2(taskId),
    onSuccess: refresh,
  });
  const stop = useMutation({
    mutationFn: () => stopTaskTimerV2(taskId),
    onSuccess: async () => {
      const active = useActiveTimerV2Store.getState();
      if (active.taskId === taskId) {
        active.setTaskId(null);
      }
      await refresh();
    },
  });

  return {
    start,
    pause,
    stop,
    isBusy: start.isPending || pause.isPending || stop.isPending,
  };
}

export function useBulkTasksV2() {
  const invalidate = useInvalidateTasksV2();

  return useMutation({
    mutationFn: (payload: TaskV2BulkRequest) => bulkTasksV2(payload),
    onSuccess: invalidate,
  });
}
